import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ConfirmHost } from "@/lib/confirm";
import { CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react";

import { AuthProvider } from "./lib/auth";
import Nav from "./components/Nav";
import StickyMobileCta from "./components/StickyMobileCta";
import GlobalVerifyEmailListener from "./components/GlobalVerifyEmailListener";
import RouteOg from "./components/RouteOg";
import Landing from "./pages/Landing";
import AdminBlock from "./components/AdminBlock";
import ErrorBoundary from "./components/ErrorBoundary";
import { ConsentBanner } from "./components/ConsentBanner";
import NotFound from "./pages/NotFound";
import api from "./lib/api";
const AcceptCoin = lazy(() => import("./pages/AcceptCoin"));
const ConvertPair = lazy(() => import("./pages/ConvertPair"));
const ConvertIndex = lazy(() => import("./pages/ConvertIndex"));
const AcceptIndex = lazy(() => import("./pages/AcceptIndex"));
const StablecoinGateway = lazy(() => import("./pages/StablecoinGateway"));
const AcceptBusiness = lazy(() => import("./pages/AcceptBusiness"));
const BusinessIndex = lazy(() => import("./pages/BusinessIndex"));
const SavingsCalculator = lazy(() => import("./pages/SavingsCalculator"));
const AcceptCountry = lazy(() => import("./pages/AcceptCountry"));
const CaseStudyHackastra = lazy(() => import("./pages/CaseStudyHackastra"));
const CaseStudyHackingCommunity = lazy(() => import("./pages/CaseStudyHackingCommunity"));
const CaseStudyWhomi = lazy(() => import("./pages/CaseStudyWhomi"));
const CaseStudiesIndex = lazy(() => import("./pages/CaseStudiesIndex"));
const ProductsHub = lazy(() => import("./pages/ProductsHub"));
const Countries = lazy(() => import("./pages/Countries"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex"));
const GuideArticle = lazy(() => import("./pages/GuideArticle"));
const Signup = lazy(() => import("./pages/Signup"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const CoinVersus = lazy(() => import("./pages/CoinVersus"));
const PublicProduct = lazy(() => import("./pages/PublicProduct"));
const PublicStorefront = lazy(() => import("./pages/PublicStorefront"));
const TrackedInvoice = lazy(() => import("./pages/TrackedInvoice"));
const InvoicePay = lazy(() => import("./pages/InvoicePay"));
const Receipt = lazy(() => import("./pages/Receipt"));
const OrderStatus = lazy(() => import("./pages/OrderStatus"));
const OrderCheckout = lazy(() => import("./pages/OrderCheckout"));
const PayLink = lazy(() => import("./pages/PayLink"));
const TipPage = lazy(() => import("./pages/TipPage"));
const Faq = lazy(() => import("./pages/Faq"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Guide = lazy(() => import("./pages/Guide"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Compliance = lazy(() => import("./pages/Compliance"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const Risk = lazy(() => import("./pages/Risk"));
const Aml = lazy(() => import("./pages/Aml"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Docs = lazy(() => import("./pages/Docs"));
const Integrations = lazy(() => import("./pages/Integrations"));
const Status = lazy(() => import("./pages/Status"));
const Switch = lazy(() => import("./pages/Switch"));
const About = lazy(() => import("./pages/About"));
const PaymentHelp = lazy(() => import("./pages/PaymentHelp"));
const Alternative = lazy(() => import("./pages/Alternative"));
const Compare = lazy(() => import("./pages/Compare"));
const BestGateways = lazy(() => import("./pages/BestGateways"));
const CryptoInvoicing = lazy(() => import("./pages/CryptoInvoicing"));
const SellProducts = lazy(() => import("./pages/SellProducts"));
const PaymentGateway = lazy(() => import("./pages/PaymentGateway"));
const PricePrediction = lazy(() => import("./pages/PricePrediction"));
const PricePredictionIndex = lazy(() => import("./pages/PricePredictionIndex"));
const CardPage = lazy(() => import("./pages/Card"));
const OrderWidget = lazy(() => import("./pages/OrderWidget"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CryptoCheckout = lazy(() => import("./pages/CryptoCheckout"));
const Renew = lazy(() => import("./pages/Renew"));
const Changelog = lazy(() => import("./pages/Changelog"));
const Brand = lazy(() => import("./pages/Brand"));
const InvestorDeck = lazy(() => import("./pages/InvestorDeck"));
const Security = lazy(() => import("./pages/Security"));
const Fees = lazy(() => import("./pages/Fees"));
const Dashboard = lazy(() => import("./pages/DashboardShell"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminCohorts = lazy(() => import("./pages/AdminCohorts"));

// Ad-campaign attribution: capture utm_* params on first touch (whatever
// page the ad lands on) and persist for the session so Signup can stamp
// them onto the new account. First-touch wins — never overwritten.
(() => {
  try {
    // First-touch referrer (only external) — lets admin acquisition classify
    // untagged signups into AI / social / search channels.
    if (!sessionStorage.getItem("cryptpe_ref")) {
      const r = document.referrer || "";
      if (r && !r.includes(window.location.hostname)) {
        sessionStorage.setItem("cryptpe_ref", r.slice(0, 300));
      }
    }
    if (sessionStorage.getItem("cryptpe_utm")) return;
    const qs = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
      const v = (qs.get(k) || "").slice(0, 100);
      if (v) utm[k] = v;
    }
    if (Object.keys(utm).length) sessionStorage.setItem("cryptpe_utm", JSON.stringify(utm));
  } catch { /* storage unavailable — attribution is best-effort */ }
})();

function ScrollToTop() {
  const location = useLocation();
  // Schema-leak guard: SPA-fallback routes (profiles, storefronts, invoices)
  // are served the homepage template, which carries homepage-only JSON-LD
  // (SoftwareApplication + FAQ). Strip it when we're not actually on "/".
  // Prerendered routes never contain data-pr-swapp, so they're untouched.
  useEffect(() => {
    if (location.pathname === "/") return;
    if (!document.querySelector("script[data-pr-swapp]")) return;
    document.querySelectorAll("script[data-pr-swapp], script[data-pr-ld]").forEach((s) => s.remove());
  }, [location.pathname]);
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function RouteNoindex() {
  const location = useLocation();
  useEffect(() => {
    const priv = /^\/(admin|dashboard)(\/|$)/.test(location.pathname);
    if (!priv) return;
    let m = document.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex, nofollow");
  }, [location.pathname]);
  return null;
}

function PageviewBeacon() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (p.startsWith("/admin") || p.startsWith("/dashboard")) return;
    api.post("/track/pageview", { path: p }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function NavWrapper() {
  const location = useLocation();
  // Hide nav on login/signup and on any public-profile route (/:username).
  // Public profiles get their own self-contained chrome (logo in the
  // terminal title bar) so we keep these pages distraction-free.
  const HIDE_EXACT = new Set(["/signup", "/login", "/dashboard", "/admin", "/investors"]);
  const KNOWN_ROUTES = new Set(["/", "/signup", "/login", "/dashboard", "/faq", "/guide", "/about", "/payment-help", "/terms", "/privacy", "/compliance", "/disclaimer", "/risk", "/aml", "/cookies", "/docs", "/integrations", "/status", "/switch", "/card", "/coinbase-commerce-alternative", "/nowpayments-alternative", "/bitpay-alternative", "/stripe-crypto-alternative", "/cryptopay-alternative", "/coingate-alternative", "/btcpay-server-alternative", "/compare", "/accept-crypto-for", "/case-studies", "/case-study/hackastra", "/case-study/hacking-community", "/case-study/whomi", "/crypto-invoicing", "/products", "/sell-products-for-crypto", "/crypto-payment-gateway", "/pricing", "/changelog", "/security", "/admin", "/admin/cohorts"]);
  // /o/{order_id} is also a known route but with a dynamic segment; the
  // first-segment check below handles the prefix match.
  if (HIDE_EXACT.has(location.pathname)) return null;
  if (/^\/[a-z0-9_-]+$/i.test(location.pathname) && !KNOWN_ROUTES.has(location.pathname)) return null;
  // Hide nav on the dedicated invoice pages too — they own their own chrome.
  if (/^\/[a-z0-9_-]+\/pay\/?$/i.test(location.pathname)) return null;
  if (/^\/i\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  // /r/{txhash} — public payment receipt; owns its own chrome.
  if (/^\/r\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  if (/^\/checkout\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  // /l/{link_id} — public payment link landing; owns its own chrome.
  if (/^\/l\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  // /tip/{username} — public tip page; owns its own chrome.
  if (/^\/tip\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  // /o/{order_id} — dedicated live-status page; also iframed into
  // merchant checkouts, so it owns its own chrome.
  if (/^\/o\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  if (/^\/[a-z0-9_-]+\/p\/[a-z0-9_-]+\/?$/i.test(location.pathname)) return null;
  if (/^\/[a-z0-9_-]+\/products\/?$/i.test(location.pathname) && !KNOWN_ROUTES.has(location.pathname)) return null;
  // /widget/* are public embeddable surfaces — they live inside merchant
  // iframes and own their own minimal chrome.
  if (/^\/widget\//i.test(location.pathname)) return null;
  return <><Nav /><StickyMobileCta /></>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
            <AuthProvider>
              <ScrollToTop />
              <RouteNoindex />
              <PageviewBeacon />
              <NavWrapper />
              <RouteOg />
              <GlobalVerifyEmailListener />
              <Suspense fallback={<div role="status" aria-busy="true" aria-label="loading page" className="min-h-[100svh] bg-black flex items-center justify-center"><img src="/logo.png" alt="loading" className="w-16 h-16 boot-emblem" /><span className="sr-only">loading…</span></div>}>
              <div id="main" tabIndex={-1} className="outline-none">
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/dashboard" element={<AdminBlock><Dashboard /></AdminBlock>} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/guide" element={<Guide />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/compliance" element={<Compliance />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/risk" element={<Risk />} />
                <Route path="/aml" element={<Aml />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/status" element={<Status />} />
                <Route path="/switch" element={<Switch />} />
                <Route path="/about" element={<About />} />
                <Route path="/case-studies" element={<CaseStudiesIndex />} />
                <Route path="/case-study/hackastra" element={<CaseStudyHackastra />} />
                <Route path="/case-study/hacking-community" element={<CaseStudyHackingCommunity />} />
                <Route path="/case-study/whomi" element={<CaseStudyWhomi />} />
                <Route path="/payment-help" element={<PaymentHelp />} />
                <Route path="/card" element={<CardPage />} />
                <Route path="/coinbase-commerce-alternative" element={<Alternative competitor="coinbase-commerce" />} />
                <Route path="/nowpayments-alternative" element={<Alternative competitor="nowpayments" />} />
                <Route path="/bitpay-alternative" element={<Alternative competitor="bitpay" />} />
                <Route path="/stripe-crypto-alternative" element={<Alternative competitor="stripe-crypto" />} />
                <Route path="/cryptopay-alternative" element={<Alternative competitor="cryptopay" />} />
                <Route path="/coingate-alternative" element={<Alternative competitor="coingate" />} />
                <Route path="/btcpay-server-alternative" element={<Alternative competitor="btcpay-server" />} />
                <Route path="/best-crypto-payment-gateway" element={<BestGateways />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/crypto-invoicing" element={<CryptoInvoicing />} />
                <Route path="/products" element={<ProductsHub />} />
                <Route path="/sell-products-for-crypto" element={<SellProducts />} />
                <Route path="/crypto-payment-gateway" element={<PaymentGateway />} />
                <Route path="/price-prediction" element={<PricePredictionIndex />} />
                <Route path="/versus/:pair" element={<CoinVersus />} />
                <Route path="/price-prediction/:slug" element={<PricePrediction />} />
                <Route path="/accept" element={<AcceptIndex />} />
                <Route path="/stablecoin-payment-gateway" element={<StablecoinGateway />} />
                <Route path="/accept/:slug" element={<AcceptCoin />} />
                <Route path="/convert" element={<ConvertIndex />} />
                <Route path="/convert/:slug" element={<ConvertPair />} />
                <Route path="/accept-crypto-for" element={<BusinessIndex />} />
                <Route path="/accept-crypto-for/:slug" element={<AcceptBusiness />} />
                <Route path="/savings-calculator" element={<SavingsCalculator />} />
                <Route path="/accept-crypto-in/:slug" element={<AcceptCountry />} />
                <Route path="/countries" element={<Countries />} />
                <Route path="/guides" element={<GuidesIndex />} />
                <Route path="/guides/:slug" element={<GuideArticle />} />
                <Route path="/pricing" element={<AdminBlock><Pricing /></AdminBlock>} />
                <Route path="/renew" element={<AdminBlock><Renew /></AdminBlock>} />
                <Route path="/billing/checkout/:order_id" element={<AdminBlock><CryptoCheckout /></AdminBlock>} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/brand" element={<Brand />} />
                <Route path="/investors" element={<InvestorDeck />} />
                <Route path="/security" element={<Security />} />
                <Route path="/fees" element={<Fees />} />
                <Route path="/o/:order_id" element={<OrderStatus />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/cohorts" element={<AdminCohorts />} />
                <Route path="/checkout/:order_id" element={<OrderCheckout />} />
                <Route path="/l/:link_id" element={<PayLink />} />
                <Route path="/tip/:username" element={<TipPage />} />
                <Route path="/widget/order/:order_id" element={<OrderWidget />} />
                <Route path="/:username/pay" element={<ErrorBoundary><InvoicePay /></ErrorBoundary>} />
                <Route path="/r/:txHash" element={<ErrorBoundary><Receipt /></ErrorBoundary>} />
                <Route path="/i/:invoiceId" element={<ErrorBoundary><TrackedInvoice /></ErrorBoundary>} />
                <Route path="/:username/p/:slug" element={<PublicProduct />} />
                <Route path="/:username/products" element={<PublicStorefront />} />
                <Route path="/:username" element={<PublicProfile />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </div>
              </Suspense>
              <ConfirmHost />
              <ConsentBanner />
              <Toaster
                theme="dark"
                position="top-center"
                gap={8}
                icons={{
                  success: <CheckCircle2 size={15} color="#fafafa" />,
                  error: <AlertCircle size={15} color="#f87171" />,
                  info: <Info size={15} color="#a1a1aa" />,
                  warning: <AlertTriangle size={15} color="#fbbf24" />,
                }}
                toastOptions={{
                  duration: 2500,
                  classNames: {
                    title: "!leading-snug",
                    description: "!text-zinc-400 !text-[12px] !leading-relaxed !mt-1 !font-sans !tracking-normal",
                    icon: "!self-start !mt-0.5",
                  },
                  style: {
                    background: "rgba(9,9,11,0.92)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid #27272a",
                    color: "#fafafa",
                    borderRadius: "16px",
                    padding: "12px 18px",
                    fontSize: "13px",
                    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    letterSpacing: "0.02em",
                    width: "fit-content",
                    maxWidth: "min(440px, 92vw)",
                    minWidth: "0",
                    margin: "0 auto",
                    textAlign: "left",
                    alignItems: "flex-start",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.65)",
                  },
                }}
              />
            </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
